import {createSlice, PayloadAction} from '@reduxjs/toolkit'
import {push} from 'connected-react-router'
import {takeEvery, getContext, call, put} from 'typed-redux-saga'
import Api from '../../api/reactpt'
import User from '../../models/User'
import {DataState} from '../../types/dataState.type'
import {actions as actionActions} from './actions'
import {actions as actionTypeActions} from './actionTypes'
import {actions as activityActions} from './activities'
import {actions as bodyAreaActions} from './bodyAreas'
import {actions as equipmentActions} from './equipment'
import {actions as goalActions} from './goals'
import {actions as routineActions} from './routines'
import {actions as userActions} from './users'

const currentUserJson = localStorage.getItem('currentUser')
const currentUser = currentUserJson ? JSON.parse(currentUserJson) : undefined

export type AuthState = {
  currentUser?: User
  dataState: DataState
}
const initialState: AuthState = {
  currentUser: currentUser,
  dataState: {state: currentUser ? 'loaded' : 'unloaded'},
}

export const {actions, reducer} = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    login: (_state: AuthState, _action: PayloadAction<{email: string; password: string}>) => {},
    setCurrentUser: (state: AuthState, action: PayloadAction<{user?: User}>) => {
      const {user} = action.payload
      if (user) {
        localStorage.setItem('currentUser', JSON.stringify(user))
      } else {
        localStorage.removeItem('currentUser')
      }
      return {...state, currentUser: user}
    },
    setDataState: (state: AuthState, action: PayloadAction<{dataState: DataState}>) => {
      const {dataState} = action.payload
      return {...state, dataState}
    },
    logout: (state: AuthState, action: PayloadAction<void>) => {},
  },
})

const clearAll = function* () {
  yield* put(actionActions.clear())
  yield* put(actionTypeActions.clear())
  yield* put(activityActions.clear())
  yield* put(bodyAreaActions.clear())
  yield* put(equipmentActions.clear())
  yield* put(goalActions.clear())
  yield* put(routineActions.clear())
  yield* put(userActions.clear())
}

export const loginSaga = function* (action: ReturnType<typeof actions.login>) {
  const {email, password} = action.payload
  const api: Api = yield* getContext<Api>('api')
  yield* put(actions.setDataState({dataState: {state: 'loading'}}))
  try {
    yield* clearAll()
    const user = yield* call(api.login, email, password)
    yield* put(actions.setCurrentUser({user}))
    yield* put(actionTypeActions.getActionTypes())
    yield* put(activityActions.getActivities())
    yield* put(bodyAreaActions.getBodyAreas())
    yield* put(equipmentActions.getEquipment())
    yield* put(goalActions.getGoals())
    yield* put(actions.setDataState({dataState: {state: 'loaded'}}))
    yield* put(push('/actions'))
  } catch (error) {
    yield* put(actions.setDataState({dataState: {state: 'unloaded', error}}))
  }
}

export const logoutSaga = function* () {
  yield* put(actions.setCurrentUser({user: undefined}))
  yield* clearAll()
  yield* put(push('/login'))
}

export const saga = function* () {
  yield* takeEvery(actions.login.type, loginSaga)
  yield* takeEvery(actions.logout.type, logoutSaga)
}
